/**
 * Badge Showcase Section
 * Display selected badges with rarity glow effects
 */

import type { Badge } from '@/config/tisa-settings';

interface BadgeShowcaseProps {
  showcaseBadges: Array<{ badge: Badge; highestTier: string }>;
}

export function BadgeShowcaseSection({ showcaseBadges }: BadgeShowcaseProps) {
  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'legendary':
        return {
          bg: 'bg-gradient-to-br from-yellow-900/50 to-amber-900/50',
          border: 'border-yellow-500',
          glow: 'shadow-[0_0_20px_rgba(251,191,36,0.5)]',
          text: 'text-yellow-300'
        };
      case 'platinum':
        return {
          bg: 'bg-gradient-to-br from-cyan-900/50 to-blue-900/50',
          border: 'border-cyan-400',
          glow: 'shadow-[0_0_15px_rgba(34,211,238,0.4)]',
          text: 'text-cyan-300'
        };
      case 'gold':
        return {
          bg: 'bg-gradient-to-br from-yellow-900/50 to-orange-900/50',
          border: 'border-yellow-600',
          glow: 'shadow-[0_0_12px_rgba(250,204,21,0.4)]',
          text: 'text-yellow-400'
        };
      case 'silver':
        return {
          bg: 'bg-gradient-to-br from-gray-700/50 to-gray-800/50',
          border: 'border-gray-400',
          glow: 'shadow-[0_0_10px_rgba(156,163,175,0.3)]',
          text: 'text-gray-300'
        };
      case 'bronze':
        return {
          bg: 'bg-gradient-to-br from-orange-900/50 to-brown-900/50',
          border: 'border-orange-700',
          glow: '',
          text: 'text-orange-400'
        };
      default:
        return {
          bg: 'bg-gray-900/50',
          border: 'border-gray-600',
          glow: '',
          text: 'text-gray-400'
        };
    }
  };

  return (
    <div className="bg-gray-900/50 border-2 border-purple/30 rounded-xl p-6">
      <h2 className="text-xl font-cinzel font-bold text-purple mb-4 flex items-center gap-2">
        🏆 Achievements
      </h2>

      {showcaseBadges.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <div className="text-4xl mb-2">🎖️</div>
          <p>No badges showcased yet</p>
          <p className="text-sm mt-1">Earn badges and add them to your showcase!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {showcaseBadges.map(({ badge, highestTier }, index) => {
            const tierStyle = getTierColor(highestTier);

            return (
              <div
                key={index}
                className={`${tierStyle.bg} ${tierStyle.glow} border-2 ${tierStyle.border} rounded-lg p-4 text-center transition-all hover:scale-105`}
              >
                {/* Badge Icon */}
                <div className="text-5xl mb-2 drop-shadow-lg">
                  {badge.icon}
                </div>

                {/* Badge Name */}
                <h3 className="text-white font-semibold font-poppins text-sm leading-tight mb-2">
                  {badge.name}
                </h3>

                {/* Tier Badge */}
                <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase ${tierStyle.text} bg-black/30 border border-current/50`}>
                  {highestTier}
                </div>

                {/* Description */}
                <p className="text-xs text-gray-400 mt-2 leading-tight">
                  {badge.description}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {showcaseBadges.length > 0 && showcaseBadges.length < 6 && (
        <div className="mt-4 text-center text-sm text-gray-400">
          You can showcase up to 6 badges ({6 - showcaseBadges.length} slots remaining)
        </div>
      )}
    </div>
  );
}
